import React, { FC, useState, ChangeEvent, useEffect } from "react";
import {
  EmailDraft,
  EmailTemplate,
  SendEmailFormData,
  SendEmailFormErrors,
} from "../../types/components";
import FileUploader from "../../components/ui/FileUploader";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import Button from "../../components/ui/Button";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Box,
  Typography,
} from "@mui/material";
import Select, { ActionMeta, MultiValue } from "react-select";
import broadcastEmail from "../../services/broadcastEmail.service";
import { usePagination } from "../../hooks/usePagination";
import Pagination from "../../components/ui/pagination";
import Toast from "../../components/ui/Toast";
import { BroadcastEmailRequest } from "../../types/api";

type OptionType = {
  value: string;
  label: string;
};

type ModalData = EmailDraft | EmailTemplate | null;

const templateToDraft = (template: EmailTemplate): EmailDraft => {
  return {
    id: template.id,
    subject: template.subject,
    body: template.body,
    signature: template.signature,
    recipientCategory: template.recipientCategory,
    toEmails: template.toEmail || "",
    ccEmails: template.ccEmail || "",
    createdAt: template.createdAt,
  };
};

const isEmailTemplate = (data: ModalData): data is EmailTemplate => {
  return data !== null && "name" in data;
};

// New Modal for Send Email Options
const SendEmailOptionsModal: FC<{
  isOpen: boolean;
  onClose: () => void;
  onSelectDraft: () => void;
  onSelectTemplate: () => void;
}> = ({ isOpen, onClose, onSelectDraft, onSelectTemplate }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-md w-full">
        <div className="p-6 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-900">Send Email</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-6 space-y-4">
          <button
            onClick={onSelectDraft}
            className="w-full p-4 border-2 border-dashed border-blue-300 rounded-lg text-center hover:border-blue-500 hover:bg-blue-50 transition-colors cursor-pointer"
          >
            <svg
              className="mx-auto h-8 w-8 text-blue-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <p className="mt-2 text-sm font-medium text-gray-900">
              Email Draft
            </p>
            <p className="text-xs text-gray-500">Select from existing drafts</p>
          </button>

          <button
            onClick={onSelectTemplate}
            className="w-full p-4 border-2 border-dashed border-green-300 rounded-lg text-center hover:border-green-500 hover:bg-green-50 transition-colors cursor-pointer"
          >
            <svg
              className="mx-auto h-8 w-8 text-green-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z"
              />
            </svg>
            <p className="mt-2 text-sm font-medium text-gray-900">
              Email Template
            </p>
            <p className="text-xs text-gray-500">Use a pre-defined template</p>
          </button>
        </div>
      </div>
    </div>
  );
};

// Modal for selecting draft or template
const SelectDraftTemplateModal: FC<{
  isOpen: boolean;
  onClose: () => void;
  type: "draft" | "template";
  data: (EmailDraft | EmailTemplate)[];
  onSelect: (selectedData: EmailDraft | EmailTemplate) => void;
}> = ({ isOpen, onClose, type, data, onSelect }) => {
  const [selectedId, setSelectedId] = useState<string>("");

  const handleSubmit = () => {
    if (!selectedId) {
      alert(`Please select a ${type}`);
      return;
    }

    const selectedItem = data.find((item) => item.id == selectedId);
    if (selectedItem) {
      onSelect(selectedItem);
      onClose();
    }
  };
  const handleClose = () => {
    setSelectedId("");
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-md w-full">
        <div className="p-6 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-900">
              Select {type === "draft" ? "Draft" : "Template"}
            </h2>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-6 space-y-4">
          <div className="flex flex-col">
            <label className="font-medium text-gray-700 mb-2">
              Select {type === "draft" ? "Draft" : "Template"}:
            </label>
            <select
              value={selectedId}
              onChange={(e) => setSelectedId(e.target.value)}
              className="p-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Choose a {type}...</option>
              {data.map((item) => (
                <option key={item.id} value={item.id}>
                  {type === "draft"
                    ? (item as EmailDraft).subject || "Untitled Draft"
                    : (item as EmailTemplate).subject || "Untitled Template"}
                </option>
              ))}
            </select>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
            >
              Continue
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const EditDraftModal: FC<{
  isOpen: boolean;
  onClose: () => void;
  data: ModalData;
  onSave: (data: SendEmailFormData) => void;
  mode?: "edit" | "create" | "template" | "send";
}> = ({ isOpen, onClose, data, onSave, mode = "edit" }) => {
  const options: OptionType[] = [
    { value: "Customers", label: "Customers" },
    { value: "Clients", label: "Clients" },
    { value: "Vendors", label: "Vendors" },
    { value: "Partners", label: "Partners" },
  ];

  const customStyles = {
    control: (provided: any, state: any) => ({
      ...provided,
      minHeight: "50px",
      border: "1px solid #ccc",
      borderRadius: "4px",
      "&:focus": {
        borderColor: "#87DE88",
      },
      boxShadow: state.isFocused ? "0 0 0 2px #87DE88" : "#87DE88",
      borderColor: state.isFocused ? "#87DE88" : "#ccc",
    }),
    multiValue: (provided: any) => ({
      ...provided,
      borderRadius: "16px",
      padding: "2px 8px",
      margin: "2px",
      backgroundColor: "#bbf7d0",
    }),
    multiValueLabel: (provided: any) => ({
      ...provided,
      color: "#0d790dff",
      fontWeight: "normal",
    }),
    multiValueRemove: (provided: any) => ({
      ...provided,
      color: "#666",
      ":hover": {
        backgroundColor: "#b3f5b8ff",
        color: "#060606ff",
      },
    }),
    placeholder: (provided: any) => ({
      ...provided,
      color: "#888686ff",
    }),
    option: (provided: any, state: any) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? "#21960fff"
        : state.isFocused
        ? "#c0f6bdff"
        : "white",
      color: state.isSelected
        ? "#121313ff"
        : state.isFocused
        ? "#121313ff"
        : "black",
      padding: "10px 15px",
      cursor: "pointer",
      transition: "all 0.3s ease",
      "&:hover": {
        backgroundColor: "#c0f6bdff ",
        color: "#121313ff ",
      },
      "&:focus": {
        backgroundColor: "#ff6b6b",
        color: "white",
        outline: "none",
      },
    }),
  };

  const [selectedOptions, setSelectedOptions] = useState<
    MultiValue<OptionType>
  >([]);
  const [formData, setFormData] = useState<SendEmailFormData>({
    subject: "",
    body: "",
    signature: "",
    signatureFile: null,
    recipientCategory: [],
    toEmails: "",
    ccEmails: "",
    attachments: null,
  });
  const [errors, setErrors] = useState<SendEmailFormErrors>({
    subject: "",
    body: "",
    toEmails: "",
    ccEmails: "",
    recipientCategory: "",
  });
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const getArray = (category: any): string[] => {
    if (Array.isArray(category)) return category;
    if (typeof category === "string") return [category];
    if (category) return [String(category)];
    return [];
  };
  useEffect(() => {
    if (data && isOpen) {
      let draftData: EmailDraft;

      if (isEmailTemplate(data)) {
        draftData = templateToDraft(data);
      } else {
        draftData = data;
      }

      setFormData({
        subject: draftData.subject || "",
        body: draftData.body || "",
        signature: draftData.signature || "",
        signatureFile: null,
        recipientCategory: draftData.recipientCategory || [],
        toEmails: draftData.toEmails || "",
        ccEmails: draftData.ccEmails || "",
        attachments: null,
      });
      if (
        draftData.recipientCategory &&
        Array.isArray(draftData.recipientCategory)
      ) {
        const options = draftData.recipientCategory.map((category) => ({
          value: category,
          label: category,
        }));
        setSelectedOptions(options);
      }
    } else if (!data && isOpen) {
      setFormData({
        subject: "",
        body: "",
        signature: "",
        signatureFile: null,
        recipientCategory: [],
        toEmails: "",
        ccEmails: "",
        attachments: null,
      });
      setSelectedOptions([]);
    }
  }, [data, isOpen]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleBodyChange = (value: string) => {
    setFormData((prev) => ({
      ...prev,
      body: value,
    }));
  };

  const handleSignatureChange = (value: string) => {
    setFormData((prev) => ({
      ...prev,
      signature: value,
    }));
  };

  const handleRecipientChange = (
    newValue: MultiValue<OptionType>,
    actionMeta: ActionMeta<OptionType>
  ) => {
    setSelectedOptions(newValue);
    const values = newValue.map((option) => option.value);
    setFormData((prev) => ({
      ...prev,
      recipientCategory: values,
    }));
  };

  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  const handleToEmailChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFormData((prev) => ({
      ...prev,
      toEmails: value,
    }));
    validateToEmails(value);
  };

  const handleCCEmailChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFormData((prev) => ({
      ...prev,
      ccEmails: value,
    }));
    validateCCEmails(value);
  };

  const validateToEmails = (value: string) => {
    const newErrors = { ...errors };

    if (!value.trim()) {
      newErrors.toEmails = "To Emails is required";
    } else {
      const emails = value.split(",").map((email) => email.trim());
      const invalidEmails = emails.filter((email) => !emailRegex.test(email));

      if (invalidEmails.length > 0) {
        newErrors.toEmails = "One or more email addresses are invalid";
      } else {
        newErrors.toEmails = "";
      }
    }
    setErrors(newErrors);
  };

  const validateCCEmails = (value: string) => {
    const newErrors = { ...errors };

    if (!value.trim()) {
      newErrors.ccEmails = "CC Emails is required";
    } else {
      const emails = value.split(",").map((email) => email.trim());
      const invalidEmails = emails.filter((email) => !emailRegex.test(email));

      if (invalidEmails.length > 0) {
        newErrors.ccEmails = "One or more email addresses are invalid";
      } else {
        newErrors.ccEmails = "";
      }
    }
    setErrors(newErrors);
  };
  const [toast, setToast] = useState<{
    message: string;
    type: "success" | "error" | "warning" | "info";
  } | null>(null);
  const handleSaveEmail = async (emailType: "draft" | "template" = "draft") => {
    try {
      if (!formData.subject?.trim()) {
        alert("Subject is required");
        return;
      }

      if (!formData.body?.trim()) {
        alert("Email body is required");
        return;
      }

      let recipientCategoryValue = "";
      if (Array.isArray(formData.recipientCategory)) {
        recipientCategoryValue =
          formData.recipientCategory.length > 0
            ? formData.recipientCategory.join(",")
            : "";
      } else if (typeof formData.recipientCategory === "string") {
        recipientCategoryValue = formData.recipientCategory;
      } else {
        recipientCategoryValue = "";
      }
      const EmailBody = formData.body.replace(/<[^>]*>/g, "").trim();
      const signature = formData.signature.replace(/<[^>]*>/g, "").trim();
      const emailSaveData : BroadcastEmailRequest = {
        // request: formData.subject,
        subject: formData.subject,
        body: EmailBody,
        signature: signature,
        recipientCategory: recipientCategoryValue,
        toEmails: formData.toEmails || "",
        ccEmails: formData.ccEmails || "",
        attachments: "",
        status: "Draft",
        isTemplate: emailType === "template",
        createdBy: signature,
      };
      // {
      //   id?: number;
      //   subject?: string;
      //   body?: string;
      //   signature?: string;
      //   recipientCategory?: string;
      //   toEmails?: string;
      //   ccEmails?: string;
      //   attachments?: string;
      //   status?: EmailStatus;
      //   isTemplate?: boolean;
      //   createdDate?: string;
      //   modifiedDate?: string;
      //   createdBy?: string;
      // }

      let savedEmail;
      if (emailType === "draft") {
        savedEmail = await broadcastEmail.saveDraft(emailSaveData);
        setToast({ message: "Draft saved successfully!", type: "success" });
      } else {
        savedEmail = await broadcastEmail.saveTemplates(emailSaveData);
        setToast({
          message: "Template created successfully!",
          type: "success",
        });
      }
      onClose();
      return savedEmail;
    } catch (error: any) {
      console.error(`Failed to save email ${emailType}:`, error);

      if (error.errors) {
        const errorMessages = Object.values(error.errors).flat();
        alert(`Failed to save: ${errorMessages.join(", ")}`);
      } else if (error.message) {
        alert(`Error: ${error.message}`);
      } else {
        alert(`Failed to save ${emailType}. Please try again.`);
      }
    }
  };
  const formatEmailList = (emails: string) => {
    return emails
      .split(",")
      .map((email) => email.trim())
      .filter((email) => email.length > 0)
      .join(", ");
  };

  const getFileNames = (signatureFile?: File): string[] => {
    const files: string[] = [];
    if (formData.signatureFile) {
      files.push(formData.signatureFile.name);
    }
    return files;
  };

  const getFileAttachmentNames = (attachments: FileList): string[] => {
    const files: string[] = [];
    if (formData.attachments) {
      const attachmentFiles = Array.from(formData.attachments);
      attachmentFiles.forEach((file) => {
        files.push(file.name);
      });
    }
    return files;
  };

  const handleSendEmail = () => {
    let emailData : BroadcastEmailRequest = {
      ...formData,
      recipientCategory : JSON.stringify(formData.recipientCategory),
      attachments : JSON.stringify([])
    }
    if (formData.attachments && formData.attachments.length > 0) {
      emailData["attachments"] = JSON.stringify(formData.attachments);
    } 
    const sendEmail = broadcastEmail.sendEmail(emailData);
    setToast({ message: "send email successfully...", type: "success" });
    setTimeout(() => {
      onClose();
    }, 2000);
  };

  const getModalTitle = () => {
    switch (mode) {
      case "create":
        return "Create New Template";
      case "template":
        return "Edit Template";
      case "send":
        return "Send Email";
      default:
        return "Edit Draft";
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] flex flex-col">
        <div className="p-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-900">
              {getModalTitle()}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          <form className="p-6 space-y-6">
            <div className="flex flex-col">
              <label className="font-medium text-gray-700">Subject</label>
              <input
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                className="p-2 mt-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter email subject"
              />
              {errors.subject && (
                <div className="text-red-500 text-sm mt-1">
                  {errors.subject}
                </div>
              )}
            </div>

            <div className="flex flex-col">
              <label className="font-medium text-gray-700">Email Body</label>
              <ReactQuill
                theme="snow"
                value={formData.body}
                onChange={handleBodyChange}
                className="p-2 mt-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                style={{ height: "140px" }}
                placeholder="Enter email content"
              />
            </div>
            {errors.body && (
              <div className="text-red-500 text-sm mt-1">{errors.body}</div>
            )}
            <br />
            <div className="flex flex-col">
              <label className="font-medium text-gray-700">
                Signature (Optional)
              </label>
              <ReactQuill
                theme="snow"
                value={formData.signature}
                onChange={handleSignatureChange}
                className="p-2 mt-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter signature"
              />
            </div>

            <div className="flex flex-col">
              <label className="font-medium text-gray-700">
                Recipient Category
              </label>
              <Select
                isMulti
                options={options}
                value={selectedOptions}
                onChange={handleRecipientChange}
                className="skills-multi-select"
                classNamePrefix="select"
                placeholder="Add Recipient Category"
                styles={customStyles}
                components={{
                  DropdownIndicator: () => null,
                  IndicatorSeparator: () => null,
                }}
              />
              {errors.recipientCategory && (
                <div className="text-red-500 text-sm mt-1">
                  {errors.recipientCategory}
                </div>
              )}
            </div>

            <div className="flex flex-col">
              <label className="font-medium text-gray-700">
                To Emails (comma separated)
              </label>
              <input
                type="text"
                name="toEmails"
                value={formData.toEmails}
                onChange={handleToEmailChange}
                className="p-2 mt-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="email1@example.com, email2@example.com"
              />
              {errors.toEmails && (
                <div className="text-red-500 text-sm mt-1">
                  {errors.toEmails}
                </div>
              )}
            </div>

            <div className="flex flex-col">
              <label className="font-medium text-gray-700">
                CC Emails (comma separated)
              </label>
              <input
                type="text"
                name="ccEmails"
                value={formData.ccEmails}
                onChange={handleCCEmailChange}
                className="p-2 mt-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="cc1@example.com, cc2@example.com"
              />
              {errors.ccEmails && (
                <div className="text-red-500 text-sm mt-1">
                  {errors.ccEmails}
                </div>
              )}
            </div>
          </form>
        </div>

        <div className="p-6 border-t border-gray-200">
          <div>
            {/* Action buttons for template mode */}
            {mode === "template" && (
              <div className="flex justify-between">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => handleSaveEmail("template")}
                  className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
                >
                  save template
                </button>
              </div>
            )}

            {/* Buttons for create template mode */}
            {mode === "create" && (
              <div className="flex justify-between">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-gray-600 border border-primary-lightGreen rounded-md hover:bg-gray-50 transition-colors hover:bg-primary-lightGreen hover:text-white"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => handleSaveEmail("draft")}
                  className="px-4 py-2 text-gray-600 border border-primary-lightGreen rounded-md hover:bg-gray-50 transition-colors hover:bg-primary-lightGreen hover:text-white"
                >
                  Save as draft
                </button>
                <button
                  type="button"
                  onClick={() => handleSaveEmail("template")}
                  className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors"
                >
                  Create Template
                </button>
              </div>
            )}

            {/* Buttons for send email mode */}
            {mode === "send" && (
              <div className="flex justify-between">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSendEmail}
                  className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
                >
                  Send Email
                </button>
              </div>
            )}

            {/* Default buttons for edit mode */}
            {mode === "edit" && (
              <div className="flex justify-between mt-4">
                <button
                  type="button"
                  onClick={() => setIsPreviewOpen(true)}
                  className="px-4 py-2 text-blue-600 border border-blue-300 rounded-md hover:bg-blue-50 transition-colors"
                >
                  Preview Email
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSendEmail}
                  className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
                >
                  send Email
                </button>
              </div>
            )}
            <Dialog
              open={isPreviewOpen}
              onClose={() => setIsPreviewOpen(false)}
              maxWidth="lg"
              fullWidth
            >
              <DialogTitle>Email Preview</DialogTitle>
              <DialogContent>
                <Box className="space-y-4">
                  <Card>
                    <CardHeader title="Email Details" />
                    <CardContent>
                      <Box className="space-y-3">
                        <div>
                          <Typography variant="subtitle2" color="textSecondary">
                            Subject
                          </Typography>
                          <Typography variant="body1">
                            {formData.subject || "No subject"}
                          </Typography>
                        </div>

                        <div>
                          <Typography variant="subtitle2" color="textSecondary">
                            Recipient Category
                          </Typography>
                          <Box className="mt-1 flex flex-wrap gap-1">
                            {getArray(formData.recipientCategory).length > 0 ? (
                              getArray(formData.recipientCategory).map(
                                (category, index) => (
                                  <Chip
                                    key={index}
                                    label={category}
                                    size="small"
                                    variant="outlined"
                                  />
                                )
                              )
                            ) : (
                              <Typography variant="body2" color="textSecondary">
                                No category selected
                              </Typography>
                            )}
                          </Box>
                        </div>

                        <Box className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <Typography
                              variant="subtitle2"
                              color="textSecondary"
                            >
                              To
                            </Typography>
                            <Typography variant="body2" className="break-words">
                              {formData.toEmails
                                ? formatEmailList(formData.toEmails)
                                : "No recipients"}
                            </Typography>
                          </div>

                          <div>
                            <Typography
                              variant="subtitle2"
                              color="textSecondary"
                            >
                              CC
                            </Typography>
                            <Typography variant="body2" className="break-words">
                              {formData.ccEmails
                                ? formatEmailList(formData.ccEmails)
                                : "No CC recipients"}
                            </Typography>
                          </div>
                        </Box>

                        {(formData.signatureFile || formData.attachments) && (
                          <div>
                            <Typography
                              variant="subtitle2"
                              color="textSecondary"
                            >
                              Attachments
                            </Typography>
                            <Box className="mt-1 space-y-1">
                              {formData.attachments &&
                                getFileAttachmentNames(
                                  formData.attachments
                                ).map((fileName, index) => (
                                  <Typography
                                    key={index}
                                    variant="body2"
                                    color="textSecondary"
                                  >
                                    📎 {fileName}
                                  </Typography>
                                ))}
                            </Box>
                          </div>
                        )}
                      </Box>
                    </CardContent>
                  </Card>

                  {/* Email Content Preview */}
                  <Card>
                    <CardHeader title="Email Content" />
                    <CardContent>
                      <Box className="border rounded-lg p-4 bg-gray-50 min-h-[200px]">
                        <div
                          className="prose prose-sm max-w-none"
                          dangerouslySetInnerHTML={{
                            __html:
                              formData.body || "<p>No email body content</p>",
                          }}
                        />

                        {formData.signature && (
                          <>
                            <Box className="mt-6 pt-4 border-t">
                              <div
                                className="prose prose-sm max-w-none"
                                dangerouslySetInnerHTML={{
                                  __html: formData.signature,
                                }}
                              />
                            </Box>
                            {formData.signatureFile &&
                              getFileNames(formData.signatureFile).map(
                                (fileName, index) => (
                                  <Typography
                                    key={index}
                                    variant="body2"
                                    color="textSecondary"
                                  >
                                    📎 {fileName}
                                  </Typography>
                                )
                              )}
                          </>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Box>
              </DialogContent>
              <DialogActions>
                <button
                  className="hover:text-primary-lightGreen border-2 border-primary-lightGreen rounded-xl px-2 lg:px-5 hover:-translate-y-0.5 active:translate-y-0 border-2 border-primary-lightGreen text-primary-lightGreen bg-transparent hover:bg-primary-lightGreen hover:text-white h-10"
                  onClick={() => setIsPreviewOpen(false)}
                >
                  Close
                </button>
                <button
                  className="bg-blue-500 text-white px-4 py-2 rounded-md shadow-sm hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  onClick={handleSendEmail}
                >
                  Send Email
                </button>
              </DialogActions>
            </Dialog>
          </div>
        </div>
      </div>
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          position="top-right"
          duration={3000}
          onClose={() => setToast(null)}
          show
        />
      )}
    </div>
  );
};

const BroadcastEmail: FC = () => {
  const [drafts, setDrafts] = useState<EmailDraft[]>([]);
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [sentEmails, setSentEmails] = useState<any[]>([]);
  const [toast, setToast] = useState<{
    message: string;
    type: "success" | "error" | "warning" | "info";
  } | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [itemsPerPage, setItemsPerPage] = useState<number>(5);

  const draftsPagination = usePagination({
    data: drafts,
    itemsPerPage,
  });

  const templatesPagination = usePagination({
    data: templates,
    itemsPerPage,
  });

  const sentEmailsPagination = usePagination({
    data: sentEmails,
    itemsPerPage,
  });

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isTemplateEditModalOpen, setIsTemplateEditModalOpen] = useState(false);
  const [selectedData, setSelectedData] = useState<ModalData>(null);
  const [isCreateTemplateModalOpen, setIsCreateTemplateModalOpen] =
    useState(false);
  const [isSendEmailOptionsModalOpen, setIsSendEmailOptionsModalOpen] =
    useState(false);
  const [isSelectDraftModalOpen, setIsSelectDraftModalOpen] = useState(false);
  const [isSelectTemplateModalOpen, setIsSelectTemplateModalOpen] =
    useState(false);
  const [isSendEmailModalOpen, setIsSendEmailModalOpen] = useState(false);

  const handleEditDraft = (draft: EmailDraft) => {
    setSelectedData(draft);
    setIsEditModalOpen(true);
  };

  const handleEditTemplate = (template: EmailTemplate) => {
    setSelectedData(template);
    setIsTemplateEditModalOpen(true);
  };

  const handleCreateNewTemplate = () => {
    setSelectedData(null);
    setIsCreateTemplateModalOpen(true);
  };

  const handleSendEmailClick = () => {
    setIsSendEmailOptionsModalOpen(true);
  };

  const handleSelectDraftOption = () => {
    setIsSendEmailOptionsModalOpen(false);
    setIsSelectDraftModalOpen(true);
  };

  const handleSelectTemplateOption = () => {
    setIsSendEmailOptionsModalOpen(false);
    setIsSelectTemplateModalOpen(true);
  };

  const handleDraftSelect = (selectedData: EmailDraft | EmailTemplate) => {
    setSelectedData(selectedData);
    setIsSelectDraftModalOpen(false);
    setIsSendEmailModalOpen(true);
  };

  const handleTemplateSelect = (selectedData: EmailDraft | EmailTemplate) => {
    setSelectedData(selectedData);
    setIsSelectTemplateModalOpen(false);
    setIsSendEmailModalOpen(true);
  };

  const handleUpdateDraft = (updatedData: SendEmailFormData) => {
    setToast({ message: "Draft updated successfully!", type: "success" });
  };

  const handleUpdateTemplate = (updatedData: SendEmailFormData) => {
    setToast({ message: "Template updated successfully!", type: "success" });
  };

  const handleCreateTemplate = (templateData: SendEmailFormData) => {
    setToast({ message: "Template created successfully!", type: "success" });
  };

  const handleSendEmail = (emailData: SendEmailFormData) => {
    setToast({ message: "Email sent successfully!", type: "success" });
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    // Reset all paginations to first page when changing page size
    draftsPagination.goToPage(1);
    templatesPagination.goToPage(1);
    sentEmailsPagination.goToPage(1);
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const draftsData = await broadcastEmail.getDrafts();
      const templatesData = await broadcastEmail.getTemplates();
      const sentEmailsData = await broadcastEmail.getSentEmails();
      const extractDataArray = (response: any): any[] => {
        if (Array.isArray(response)) return response;
        if (response?.data?.data && Array.isArray(response.data.data))
          return response.data.data;
        if (response?.data && Array.isArray(response.data))
          return response.data;
        if (response?.items && Array.isArray(response.items))
          return response.items;
        return [];
      };
      const getDrafts = extractDataArray(draftsData);
      const getTemplates = extractDataArray(templatesData);
      const getEmailHistory = extractDataArray(sentEmailsData);
      setDrafts(getDrafts);
      setTemplates(getTemplates);
      setSentEmails(getEmailHistory);
    } catch (error) {
      console.error("Error loading data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDraft = async (draftId: string) => {
    try {
      setLoading(true);
      await broadcastEmail.deleteDraft(parseInt(draftId));
      setDrafts((prev) => prev.filter((draft) => draft.id !== draftId));
      setToast({ message: "Draft deleted successfully", type: "success" });
    } catch (error) {
      console.error("Error deleting draft:", error);
      setToast({ message: "Error deleting draft", type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTemplate = async (templateId: string) => {
    try {
      setLoading(true);
      await broadcastEmail.deleteTemplate(parseInt(templateId));
      setTemplates((prev) =>
        prev.filter((template) => template.id !== templateId)
      );
      setToast({ message: "Template deleted successfully", type: "success" });
    } catch (error) {
      console.error("Error deleting template:", error);
      setToast({ message: "Error deleting template", type: "error" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 p-8">
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Quick Actions
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div
            className="p-4 border-2 border-dashed border-gray-300 rounded-lg text-center hover:border-primary-lightGreen hover:bg-primary-lightGreen/10 transition-colors cursor-pointer"
            onClick={handleCreateNewTemplate}
          >
            <svg
              className="mx-auto h-8 w-8 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 6v6m0 0v6m0-6h6m-6 0H6"
              />
            </svg>
            <p className="mt-2 text-sm font-medium text-gray-900">
              Create New Template
            </p>
          </div>
          <div
            className="p-4 border-2 border-dashed border-gray-300 rounded-lg text-center hover:border-green-400 hover:bg-green-50 transition-colors cursor-pointer"
            onClick={handleSendEmailClick}
          >
            <svg
              className="mx-auto h-8 w-8 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <p className="mt-2 text-sm font-medium text-gray-900">Send Email</p>
          </div>
        </div>
      </div>
      <br />

      {/* Drafts Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-lg font-semibold text-gray-900">
            Draft Emails ({drafts && Array.isArray(drafts) ? drafts.length : 0})
          </h2>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Subject
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Recipients
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {draftsPagination &&
              Array.isArray(drafts) &&
              draftsPagination.currentItems.length > 0 ? (
                draftsPagination.currentItems.map((draft) => (
                  <tr key={draft.id} className="hover:bg-primary-teaGreen/30">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {draft.subject || "No subject"}
                      </div>
                      <div className="text-sm text-gray-500 truncate max-w-xs">
                        {draft.body
                          ? draft.body
                              .replace(/<[^>]*>/g, "")
                              .substring(0, 50) + "..."
                          : "No content"}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">
                        {draft.recipientCategory &&
                        Array.isArray(draft.recipientCategory)
                          ? draft.recipientCategory.join(", ")
                          : "No categories"}
                      </div>
                      {draft.toEmails && (
                        <div className="text-xs text-gray-400">
                          {draft.toEmails.split(",").length} recipients
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">
                        {draft.createdAt
                          ? new Date(draft.createdAt).toLocaleDateString()
                          : "N/A"}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-green-600 hover:bg-green-50"
                          iconOnly
                          aria-label="Edit Draft"
                          title="Edit Draft"
                          onClick={() => handleEditDraft(draft)}
                          icon={
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                              strokeWidth={1.5}
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M16.862 3.487a2.25 2.25 0 113.182 3.182L7.125 19.588a4.5 4.5 0 01-1.591 1.01l-3.02 1.007 1.007-3.02a4.5 4.5 0 011.01-1.591L16.862 3.487z"
                              />
                            </svg>
                          }
                        />
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-600 hover:bg-red-50"
                          iconOnly
                          aria-label="Delete"
                          title="Delete"
                          onClick={() => handleDeleteDraft(draft.id)}
                          icon={
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                              strokeWidth={1.5}
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.108 1.022.17m-1.022-.17L18.16 19.673A2.25 2.25 0 0115.917 21.75H8.083a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .567c.34-.062.68-.118 1.022-.17m0 0A48.11 48.11 0 0112 4.5c1.886 0 3.727.128 5.478.37m-12 .92L6.16 19.673A2.25 2.25 0 008.403 21.75h7.514a2.25 2.25 0 002.244-2.077L19.228 5.79m-12-.92V3.75A2.25 2.25 0 019.478 1.5h5.044A2.25 2.25 0 0116.772 3.75v1.12"
                              />
                            </svg>
                          }
                        />
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={4}
                    className="px-6 py-4 text-center text-gray-500"
                  >
                    {loading ? "Loading drafts..." : "No drafts found"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <Pagination
          currentPage={draftsPagination.currentPage}
          totalPages={draftsPagination.totalPages}
          totalItems={drafts.length}
          onPageChange={draftsPagination.goToPage}
          onNextPage={draftsPagination.goToNextPage}
          onPreviousPage={draftsPagination.goToPreviousPage}
          getPageNumbers={draftsPagination.getPageNumbers}
          itemsPerPage={itemsPerPage}
          onItemsPerPageChange={handleItemsPerPageChange}
        />
      </div>

      {/* Templates Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden mt-6">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            Email Templates (
            {templates && Array.isArray(templates) ? templates.length : 0})
          </h2>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Subject
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Categories
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {templatesPagination.currentItems &&
              Array.isArray(templates) &&
              templatesPagination.currentItems.length > 0 ? (
                templatesPagination.currentItems.map((template) => (
                  <tr
                    key={template.id}
                    className="hover:bg-primary-teaGreen/30"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {template.name || "No name"}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">
                        {template.subject || "No subject"}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">
                        {template.recipientCategory &&
                        Array.isArray(template.recipientCategory)
                          ? template.recipientCategory.join(", ")
                          : "No categories"}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-green-600 hover:bg-green-50"
                          iconOnly
                          aria-label="Edit Template"
                          title="Edit Template"
                          onClick={() => handleEditTemplate(template)}
                          icon={
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                              strokeWidth={1.5}
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M16.862 3.487a2.25 2.25 0 113.182 3.182L7.125 19.588a4.5 4.5 0 01-1.591 1.01l-3.02 1.007 1.007-3.02a4.5 4.5 0 011.01-1.591L16.862 3.487z"
                              />
                            </svg>
                          }
                        />
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-600 hover:bg-red-50"
                          iconOnly
                          aria-label="Delete"
                          title="Delete"
                          onClick={() => handleDeleteTemplate(template.id)}
                          icon={
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                              strokeWidth={1.5}
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.108 1.022.17m-1.022-.17L18.16 19.673A2.25 2.25 0 0115.917 21.75H8.083a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .567c.34-.062.68-.118 1.022-.17m0 0A48.11 48.11 0 0112 4.5c1.886 0 3.727.128 5.478.37m-12 .92L6.16 19.673A2.25 2.25 0 008.403 21.75h7.514a2.25 2.25 0 002.244-2.077L19.228 5.79m-12-.92V3.75A2.25 2.25 0 019.478 1.5h5.044A2.25 2.25 0 0116.772 3.75v1.12"
                              />
                            </svg>
                          }
                        />
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={4}
                    className="px-6 py-4 text-center text-gray-500"
                  >
                    {loading ? "Loading templates..." : "No templates found"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <Pagination
          currentPage={templatesPagination.currentPage}
          totalPages={templatesPagination.totalPages}
          totalItems={templates.length}
          onPageChange={templatesPagination.goToPage}
          onNextPage={templatesPagination.goToNextPage}
          onPreviousPage={templatesPagination.goToPreviousPage}
          getPageNumbers={templatesPagination.getPageNumbers}
          itemsPerPage={itemsPerPage}
          onItemsPerPageChange={handleItemsPerPageChange}
        />
      </div>
      <br />
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            Sent Emails (
            {sentEmails && Array.isArray(sentEmails) ? sentEmails.length : 0})
          </h2>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 ">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  subject
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Sent Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Recipients
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sentEmailsPagination.currentItems &&
              Array.isArray(sentEmails) &&
              sentEmailsPagination.currentItems.length > 0 ? (
                sentEmailsPagination.currentItems.map((email) => (
                  <tr key={email.id} className="hover:bg-primary-teaGreen/30">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {email.subject || "No subject"}
                      </div>
                      <div className="text-sm text-gray-500 truncate max-w-xs">
                        {email.body
                          ? email.body
                              .replace(/<[^>]*>/g, "")
                              .substring(0, 50) + "..."
                          : "No content"}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">
                        {email.status ? email.status : "no status"}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">
                        {email.createdDate
                          ? new Date(email.createdDate).toLocaleDateString()
                          : "N/A"}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">
                        {email.recipientCategory &&
                        Array.isArray(email.recipientCategory)
                          ? email.recipientCategory.join(", ")
                          : "No categories"}
                      </div>
                      {email.toEmail && (
                        <div className="text-xs text-gray-400">
                          {email.toEmail.split(",").length} recipients
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={4}
                    className="px-6 py-4 text-center text-gray-500"
                  >
                    {loading ? "Loading drafts..." : "no sent email..."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <Pagination
          currentPage={sentEmailsPagination.currentPage}
          totalPages={sentEmailsPagination.totalPages}
          totalItems={sentEmails.length}
          onPageChange={sentEmailsPagination.goToPage}
          onNextPage={sentEmailsPagination.goToNextPage}
          onPreviousPage={sentEmailsPagination.goToPreviousPage}
          getPageNumbers={sentEmailsPagination.getPageNumbers}
          itemsPerPage={itemsPerPage}
          onItemsPerPageChange={handleItemsPerPageChange}
        />
      </div>

      <EditDraftModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        data={selectedData}
        onSave={handleUpdateDraft}
        mode="edit"
      />

      <EditDraftModal
        isOpen={isTemplateEditModalOpen}
        onClose={() => setIsTemplateEditModalOpen(false)}
        data={selectedData}
        onSave={handleUpdateTemplate}
        mode="template"
      />

      <EditDraftModal
        isOpen={isCreateTemplateModalOpen}
        onClose={() => setIsCreateTemplateModalOpen(false)}
        data={null}
        onSave={handleCreateTemplate}
        mode="create"
      />

      <SendEmailOptionsModal
        isOpen={isSendEmailOptionsModalOpen}
        onClose={() => setIsSendEmailOptionsModalOpen(false)}
        onSelectDraft={handleSelectDraftOption}
        onSelectTemplate={handleSelectTemplateOption}
      />

      <SelectDraftTemplateModal
        isOpen={isSelectDraftModalOpen}
        onClose={() => setIsSelectDraftModalOpen(false)}
        type="draft"
        data={drafts}
        onSelect={handleDraftSelect}
      />

      <SelectDraftTemplateModal
        isOpen={isSelectTemplateModalOpen}
        onClose={() => setIsSelectTemplateModalOpen(false)}
        type="template"
        data={templates}
        onSelect={handleTemplateSelect}
      />

      <EditDraftModal
        isOpen={isSendEmailModalOpen}
        onClose={() => setIsSendEmailModalOpen(false)}
        data={selectedData}
        onSave={handleSendEmail}
        mode="send"
      />
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          position="top-right"
          duration={3000}
          onClose={() => setToast(null)}
          show
        />
      )}
    </div>
  );
};

export default BroadcastEmail;
